import { FC } from 'react'

import {
  Badge,
  Box,
  Checkbox,
  Flex,
  GreyGrey30,
  GreyGrey60,
  GreyGrey100,
  InputSelectControl,
  Primary12Regular,
  Primary13Regular
} from '@sequencehq/core-components'
import { formatDistanceToNow } from '@sequencehq/utils/dates'
import { match } from 'ts-pattern'

import {
  QUOTE_RECIPIENTS_NOTIFY_LIST_COPY,
  RECIPIENT_TYPE_OPTIONS
} from './QuoteRecipientsNotifyList.constants'
import type {
  EditableRecipientType,
  QuoteRecipientRow,
  RecipientRowKind
} from './useQuoteRecipientRows'

type Props = {
  row: QuoteRecipientRow
  isSelected: boolean
  onToggle: () => void
  /**
   * Lets signer and cc rows be swapped with a small select on the right. Without
   * it the row is read-only, and the type sits next to the name as a badge.
   */
  canEditRecipientType?: boolean
  onTypeChange?: (rowId: string, type: EditableRecipientType) => void
  disabled: boolean
  /** Stops this row being ticked while leaving its type select usable. */
  isCheckboxDisabled?: boolean
  'data-testid'?: string
}

export const RecipientRow: FC<Props> = ({
  row,
  isSelected,
  onToggle,
  canEditRecipientType = false,
  onTypeChange,
  disabled,
  isCheckboxDisabled = false,
  'data-testid': dataTestId
}) => {
  // A countersigner is fixed by the quote's settings, so it stays a badge even
  // in a list where the others can be changed.
  const typeControl =
    canEditRecipientType && row.kind !== 'COUNTER_SIGNER' ? (
      <InputSelectControl
        variant="small"
        data-testid={dataTestId ? `${dataTestId}.type` : undefined}
        options={RECIPIENT_TYPE_OPTIONS}
        value={row.kind}
        onChange={value =>
          onTypeChange?.(row.id, value as EditableRecipientType)
        }
        disabled={disabled}
      />
    ) : (
      <RecipientTypeBadge
        kind={row.kind}
        data-testid={dataTestId ? `${dataTestId}.type` : undefined}
      />
    )

  const showNotifiedAt = Boolean(row.lastNotifiedAt)

  return (
    <Flex
      alignItems="center"
      gap="8px"
      padding="8px 12px"
      minHeight="40px"
      borderTop={`${GreyGrey30} 1px solid`}
      data-testid={dataTestId}
    >
      <Checkbox
        data-testid={dataTestId ? `${dataTestId}.checkbox` : undefined}
        isChecked={isSelected}
        disabled={disabled || isCheckboxDisabled}
        onChange={onToggle}
        label={
          <Box {...Primary13Regular} color={GreyGrey100}>
            {row.name}
          </Box>
        }
      />

      {/*
        Reading a row, the type belongs to the name, so it sits beside it and the
        notified caption goes out to the far edge. An editable row instead keeps
        its select on the right, where every row's lines up.
      */}
      {!canEditRecipientType && typeControl}

      {(showNotifiedAt || canEditRecipientType) && (
        <Flex marginLeft="auto" alignItems="center" gap="8px">
          {row.lastNotifiedAt && (
            <Box {...Primary12Regular} color={GreyGrey60} whiteSpace="nowrap">
              Notified {formatDistanceToNow(new Date(row.lastNotifiedAt))} ago
            </Box>
          )}

          {canEditRecipientType && typeControl}
        </Flex>
      )}
    </Flex>
  )
}

const RecipientTypeBadge: FC<{
  kind: RecipientRowKind
  'data-testid'?: string
}> = ({ kind, 'data-testid': dataTestId }) => (
  <Badge sentiment="neutralRegular" data-testid={dataTestId}>
    {match(kind)
      .with('SIGNER', () => QUOTE_RECIPIENTS_NOTIFY_LIST_COPY.kindLabel.SIGNER)
      .with('CC', () => QUOTE_RECIPIENTS_NOTIFY_LIST_COPY.kindLabel.CC)
      .with(
        'COUNTER_SIGNER',
        () => QUOTE_RECIPIENTS_NOTIFY_LIST_COPY.kindLabel.COUNTER_SIGNER
      )
      .exhaustive()}
  </Badge>
)
